import { createSelector } from 'reselect';
import {
  prop,
  values,
  path,
  propEq,
  propOr,
  pathOr,
  find,
  pathEq,
  pipe,
  groupBy,
  map,
  concat,
  propIs,
  applySpec,
  head,
} from 'ramda';

import i18n from 'services/i18n';
import { formatDate, formatDateOr } from 'utilities/formatters';
import { NA } from 'constants/common';

const localState = prop('resources');

export const isLoading = createSelector(
  localState,
  prop('loading'),
);

export const getData = createSelector(
  localState,
  prop('data'),
);

export const getFaq = createSelector(
  localState,
  prop('faq'),
);

export const isData = createSelector(
  getData,
  data => !!data,
);

export const isFaq = createSelector(
  getFaq,
  data => !!data,
);

export const isResources = createSelector(
  isFaq,
  isData,
  (faq, data) => faq && data,
);

export const getCurrentResource = createSelector(
  localState,
  prop('currentResource'),
);

export const getAllResourcesEntriesList = createSelector(
  getData,
  getFaq,
  (data, faq) => [data, faq],
);

export const getGroupTitleByGroupId = groupId =>
  createSelector(
    getAllResourcesEntriesList,
    pipe(
      find(pathEq(['identifier', '0', 'value'], groupId)),
      propOr('', 'description'),
    ),
  );

export const getAllDocumentsList = createSelector(
  getData,
  getFaq,
  (data, faq) => concat(propOr([], 'content', data), propOr([], 'content', faq)),
);

export const getResourceImage = createSelector(
  localState,
  pipe(
    path(['resourceImg', 'content', '0', 'attachment', 'data']),
    img => (img ? `data:image/png;base64,${img}` : null),
  ),
);

export const getResourceFile = createSelector(
  localState,
  path(['resourceFile', 'content', '0', 'attachment', 'data']),
);

export const getDocumentById = id =>
  createSelector(
    getAllDocumentsList,
    find(pathEq(['attachment', 'url'], id)),
  );

export const getDocumentTitleById = id =>
  createSelector(
    getDocumentById(id),
    path(['attachment', 'title']),
  );

export const getDocumentImageUrlById = id =>
  createSelector(
    getDocumentById(id),
    pipe(
      propOr([], 'extension'),
      find(propEq('url', 'imageUrl')),
      prop('valueCode'),
    ),
  );

export const getDocumentFileUrlById = id =>
  createSelector(
    getDocumentById(id),
    pipe(
      propOr([], 'extension'),
      find(propEq('url', 'fileUrl')),
      prop('valueCode'),
    ),
  );

export const getDocumentDescriptionById = id =>
  createSelector(
    getDocumentById(id),
    pipe(
      prop('extension'),
      find(propEq('url', 'description')),
      prop('valueCode'),
    ),
  );

export const getDocumentDateById = id =>
  createSelector(
    getDocumentById(id),
    pipe(
      prop('extension'),
      find(propEq('url', 'date')),
      prop('valueCode'),
    ),
  );
export const getDocumentPublishedByById = id =>
  createSelector(
    getDocumentById(id),
    pipe(
      prop('extension'),
      find(propEq('url', 'publishedBy')),
      prop('valueCode'),
    ),
  );

export const getDocumentPublishedByUrlById = id =>
  createSelector(
    getDocumentById(id),
    pipe(
      prop('extension'),
      find(propEq('url', 'publishedByUrl')),
      prop('valueCode'),
    ),
  );

export const getDocumentTypeByUrlById = id =>
  createSelector(
    getDocumentById(id),
    pipe(
      prop('extension'),
      find(propEq('url', 'type')),
      prop('valueCode'),
    ),
  );

export const getResourcesShouldShowLoader = createSelector(
  [isLoading, getData],
  (loading, data) => loading && !data,
);

export const getStatuses = createSelector(
  localState,
  prop('statuses'),
);

export const isStatusesInChange = createSelector(
  localState,
  prop('isStatusesInChange'),
);

export const getStatusesResource = createSelector(
  getStatuses,
  path(['entry', '0', 'resource']),
);

export const getResourcesLikes = createSelector(
  getStatuses,
  pipe(
    pathOr([], ['entry', '0', 'resource', 'item']),
    find(propEq('linkId', 'like')),
    propOr([], 'item'),
  ),
);

export const getResourcesLikeById = resourceId =>
  createSelector(
    getResourcesLikes,
    pipe(
      find(propEq('linkId', `like-${resourceId}`)),
      pathOr(null, ['answer', '0', 'valueBoolean']),
    ),
  );

export const getResourcesRead = createSelector(
  getStatuses,
  pipe(
    pathOr([], ['entry', '0', 'resource', 'item']),
    find(propEq('linkId', 'read')),
    propOr([], 'item'),
  ),
);

export const getResourcesReadById = resourceId =>
  createSelector(
    getResourcesRead,
    pipe(
      find(propEq('linkId', `read-${resourceId}`)),
      pathOr(null, ['answer', '0', 'valueDateTime']),
    ),
  );

export const getResourcesReadDateById = resourceId =>
  createSelector(
    getResourcesReadById(resourceId),
    formatDate(i18n.t('dateTimeFormat.readOnDate')),
  );

export const getResourcesReadTimeById = resourceId =>
  createSelector(
    getResourcesReadById(resourceId),
    formatDate(i18n.t('dateTimeFormat.readOnTime')),
  );

export const isResourcesAndStatuses = createSelector(
  getData,
  getStatuses,
  (data, statuses) => !!(data && statuses),
);

export const getStatusesProgress = createSelector(
  getStatuses,
  pipe(
    pathOr([], ['entry', '0', 'resource', 'item']),
    find(propEq('linkId', 'read')),
    propOr([], 'item'),
  ),
);

export const getSidebarDataWithStatuses = createSelector(
  getData,
  getStatusesProgress,
  (data, statuses) => {
    if (!data) {
      return null;
    }

    return [
      {
        title: prop('description', data),
        groupUrl: path(['identifier', '0', 'value'], data),
        list: pipe(
          propOr([], 'content'),
          map(article => {
            const id = path(['attachment', 'url'])(article);

            return {
              title: path(['attachment', 'title'])(article),
              id,
              read: pipe(
                find(propEq('linkId', `read-${id}`)),
                answer => (answer ? propIs(Array, 'answer')(answer) : false),
              )(statuses),
              type: pipe(
                propOr([], 'extension'),
                find(propEq('url', 'type')),
                prop('valueCode'),
              )(article),
            };
          }),
        )(data),
      },
    ];
  },
);

export const getFaqDataWithStatuses = createSelector(
  getFaq,
  getStatusesProgress,
  (data, statuses) => {
    if (!data) {
      return null;
    }

    return pipe(
      propOr([], 'content'),
      groupBy(
        pipe(
          propOr([], 'extension'),
          find(propEq('url', 'subGroup')),
          prop('valueCode'),
        ),
      ),
      values,
      map(list => ({
        subGroup: pipe(
          path(['0', 'extension']),
          find(propEq('url', 'subGroup')),
          prop('valueCode'),
        )(list),
        subGroupUrl: pipe(
          path(['0', 'extension']),
          find(propEq('url', 'subGroupUrl')),
          prop('valueCode'),
        )(list),
        list: map(article => {
          const id = path(['attachment', 'url'])(article);
          return {
            title: path(['attachment', 'title'])(article),
            id,
            read: pipe(
              find(propEq('linkId', `read-${id}`)),
              answer => (answer ? propIs(Array, 'answer')(answer) : false),
            )(statuses),
          };
        })(list),
      })),
    )(data);
  },
);

export const handleCVIData = map(
  applySpec({
    key: prop('id'),
    total: propOr(0, 'total'),
    lastUpdated: pipe(
      propOr([], 'entry'),
      head,
      pathOr(NA, ['resource', 'meta', 'lastUpdated']),
      date => formatDateOr(NA, i18n.t('dateTimeFormat.engagementDate'))(date),
    ),
    id: pipe(
      propOr([], 'entry'),
      head,
      pathOr(NA, ['resource', 'id']),
    ),
  }),
);
